import React, { useState, useEffect, useRef, useCallback } from 'react';
import doordashLogo from '../../../images/featured-carousel/Doordash.svg';
import energizerLogo from '../../../images/featured-carousel/energizer.svg';
import gilletteLogo from '../../../images/featured-carousel/Gillette.svg';
import huluLogo from '../../../images/featured-carousel/Hulu.svg';
import nestleLogo from '../../../images/featured-carousel/Nestle.svg';
import walmartLogo from '../../../images/featured-carousel/Walmart.svg';
import { ReportCard } from './ReportCard';
import { motion, AnimatePresence } from 'framer-motion';
import svgPaths from './svg/svg-6r5c7xjzbf';
import svgPathsGradient from './svg/svg-7pk991am6g';
import svgPathsAi from './svg/svg-v3t802dzz6';

/* ─── Dark Gradient Bar ─── */
function DarkGradientBar() {
  return (
    <span className="ai-builder-anim-bg-wrapper">
      <span className="ai-builder-anim-bg-header">
        {Array(4)
          .fill(1)
          .map((_, index) => (
            <span key={index} /> //eslint-disable-line
          ))}
      </span>
    </span>
  );
}

/* ─── Background glow (CSS layers — much cheaper than animated SVG blur in Firefox) ─── */
function ShadowEllipse({ active }) {
  const idleState = { x: 0, y: 0, scale: 1 };

  return (
    <>
      <motion.div
        className="featured-carousel-glow featured-carousel-glow--pink"
        style={{ left: 300, top: 200 }}
        initial={idleState}
        animate={
          active
            ? {
                x: [0, -30, 40, -20, 0],
                y: [0, 40, -30, 20, 0],
                scale: [1, 1.05, 0.95, 1.02, 1],
              }
            : idleState
        }
        transition={
          active
            ? {
                duration: 9,
                ease: 'easeInOut',
                repeat: Infinity,
                repeatType: 'loop',
              }
            : { duration: 0 }
        }
      />
      <motion.div
        className="featured-carousel-glow featured-carousel-glow--blue"
        style={{ left: -50, top: 100 }}
        initial={idleState}
        animate={
          active
            ? {
                x: [0, 50, -40, 30, 0],
                y: [0, -40, 50, -20, 0],
                scale: [1, 1.08, 0.96, 1.04, 1],
              }
            : idleState
        }
        transition={
          active
            ? {
                duration: 11,
                ease: 'easeInOut',
                repeat: Infinity,
                repeatType: 'loop',
              }
            : { duration: 0 }
        }
      />
    </>
  );
}

/* ─── Survey Question Card ── */
function QuestionCard({
  label,
  children,
  gradient,
  borderColor = 'rgba(255,255,255,0.08)',
}) {
  return (
    <div
      className="w-full relative"
      style={{
        background: gradient || 'white',
        borderRadius: 7.06,
        boxShadow:
          borderColor === 'rgba(0,0,0,0.05)'
            ? '0px 4px 20px rgba(0,0,0,0.02)'
            : 'none',
      }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ border: `0.77px solid ${borderColor}`, borderRadius: 7.06 }}
      />
      <div
        className="flex flex-col items-start"
        style={{ padding: '17px 17px 0 17px' }}
      >
        {children}
      </div>
    </div>
  );
}

function AnswerRow({
  num,
  text,
  numColor = '#9198a7',
  textColor = 'rgba(255,255,255,0.6)',
}) {
  return (
    <div
      className="flex items-start"
      style={{ gap: 8, paddingLeft: 3, width: 243, fontWeight: 400 }}
    >
      <div
        className="flex flex-col items-center justify-center flex-shrink-0"
        style={{ width: 11 }}
      >
        <p
          style={{
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 400,
            fontSize: 11,
            lineHeight: '15px',
            color: numColor,
          }}
        >
          {num}
        </p>
      </div>
      <p
        className="flex-shrink-0"
        style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 400,
          fontSize: 11,
          lineHeight: '15px',
          color: textColor,
          width: 245,
        }}
      >
        {text}
      </p>
    </div>
  );
}

/* Crossfade two language variants without layout shift.
   Both layers share one grid cell so the card keeps the taller height. */
function CrossfadeContent({ isTranslated, children }) {
  return (
    <div className="grid w-full">
      <motion.div
        style={{ gridArea: '1 / 1' }}
        className="min-w-0"
        animate={{ opacity: isTranslated ? 0 : 1 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        aria-hidden={isTranslated}
      >
        {children(false)}
      </motion.div>
      <motion.div
        style={{ gridArea: '1 / 1' }}
        className="min-w-0"
        animate={{ opacity: isTranslated ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        aria-hidden={!isTranslated}
      >
        {children(true)}
      </motion.div>
    </div>
  );
}

function SurveyPanel({ phase }) {
  const isDarkMode = false;
  const showQuestions =
    phase === 'response' ||
    phase === 'translateTyping' ||
    phase === 'translateThinking' ||
    phase === 'translated';
  const isTranslated = phase === 'translated';

  const textColor = isDarkMode ? 'rgba(255,255,255,0.6)' : '#323945';
  const borderColor = isDarkMode
    ? 'rgba(255,255,255,0.08)'
    : 'rgba(0,0,0,0.05)';
  const q1Gradient = isDarkMode
    ? 'rgba(255,255,255,0.1)'
    : 'rgba(255,255,255,0.8)';
  const q3Gradient = isDarkMode
    ? 'linear-gradient(to bottom, rgba(255,255,255,0.055) 18.29%, rgba(255,255,255,0.04) 123.56%)'
    : 'rgba(255,255,255,0.6)';
  const q5Gradient = isDarkMode
    ? 'linear-gradient(to right, rgba(255,255,255,0.02) 23.36%, rgba(255,255,255,0.055) 100%)'
    : 'rgba(255,255,255,0.4)';
  const q2Gradient = isDarkMode
    ? 'rgba(255,255,255,0.08)'
    : 'rgba(255,255,255,0.9)';
  const q4Gradient = isDarkMode
    ? 'rgba(255,255,255,0.03)'
    : 'rgba(255,255,255,0.3)';

  // Track which questions have been translated (staggered reveal)
  const [translatedIndices, setTranslatedIndices] = useState(new Set());

  useEffect(() => {
    if (isTranslated) {
      // Stagger the translation of each question card
      const order = [0, 1, 2, 3, 4]; // Q1, Q2, Q3, Q4, Q5
      const timers = [];
      order.forEach((idx, i) => {
        timers.push(
          setTimeout(() => {
            setTranslatedIndices((prev) => new Set([...prev, idx]));
          }, i * 400),
        );
      });
      return () => timers.forEach(clearTimeout);
    } else {
      setTranslatedIndices(new Set());
    }
  }, [isTranslated]);

  // English content
  const englishLeft = [
    {
      key: 'q1',
      width: 278,
      gradient: q1Gradient,
      delay: 0,
      globalIdx: 0,
      content: (translated) => (
        <div
          className="flex flex-col items-start w-full"
          style={{ gap: 16.44 }}
        >
          <div className="flex items-start w-full" style={{ gap: 8 }}>
            <span
              style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                lineHeight: '15px',
                color: textColor,
              }}
            >
              Q1
            </span>
            <p
              className="flex-1"
              style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: 11,
                lineHeight: '15px',
                color: textColor,
              }}
            >
              {translated
                ? 'Σε ποια τιμή ($USD) θα θεωρούσατε ότι το προϊόν είναι...'
                : 'At what price ($USD) would you consider the product to be...'}
            </p>
          </div>
          <div
            className="flex flex-col items-start w-full"
            style={{ gap: 9.05 }}
          >
            <AnswerRow
              num="01"
              text={
                translated
                  ? 'τόσο φθηνό που θα αμφισβητούσατε την ποιότητά του και δεν θα το εξετάζατε;'
                  : 'so inexpensive that you would question the quality and not consider it?'
              }
              textColor={textColor}
            />
            <AnswerRow
              num="02"
              text={
                translated
                  ? 'μια ευκαιρία - μια εξαιρετική αγορά για τα χρήματα;'
                  : 'is a bargain - a great buy for the money?'
              }
              textColor={textColor}
            />
            <AnswerRow
              num="03"
              text={
                translated
                  ? 'γίνεται ακριβό, αλλά μπορεί ακόμα να το σκεφτείτε;'
                  : 'is getting expensive, but you still might consider it?'
              }
              textColor={textColor}
            />
            <AnswerRow
              num="04"
              text={
                translated
                  ? 'θα αρχίζατε να πιστεύετε ότι είναι πολύ ακριβό για να το εξετάσετε;'
                  : 'would you begin to think the item is too expensive to consider?'
              }
              textColor={textColor}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'q3',
      width: 287,
      gradient: q3Gradient,
      delay: 2,
      globalIdx: 2,
      content: (translated) => (
        <div className="flex items-start w-full" style={{ gap: 8 }}>
          <span
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 700,
              fontSize: 11,
              lineHeight: '15px',
              color: textColor,
            }}
          >
            Q3
          </span>
          <p
            className="flex-1"
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: 11,
              lineHeight: '15px',
              color: textColor,
            }}
          >
            {translated
              ? 'Ποιο είναι το ελάχιστο ποσό που θα ήσασταν διατεθειμένοι να ξοδέψετε για αυτό το προϊόν/υπηρεσία;'
              : 'What is the minimum amount you would be willing to spend on this product/service?'}
          </p>
        </div>
      ),
    },
    {
      key: 'q5',
      width: 287,
      gradient: q5Gradient,
      delay: 4,
      globalIdx: 4,
      content: (translated) => (
        <div className="flex items-start w-full" style={{ gap: 8 }}>
          <span
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 700,
              fontSize: 11,
              lineHeight: '15px',
              color: textColor,
            }}
          >
            Q5
          </span>
          <p
            className="flex-1"
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: 11,
              lineHeight: '15px',
              color: textColor,
            }}
          >
            {translated
              ? 'Πώς θα αξιολογούσατε τη σχέση ποιότητας-τιμής παρόμοιων προϊόντων/υπηρεσιών που έχετε αγοράσει στο παρελθόν;'
              : 'How would you rate the value for money of similar products/services you have purchased in the past?'}
          </p>
        </div>
      ),
    },
  ];

  const englishRight = [
    {
      key: 'q2',
      width: 243,
      gradient: q2Gradient,
      delay: 1,
      globalIdx: 1,
      content: (translated) => (
        <div className="flex items-start w-full" style={{ gap: 8 }}>
          <span
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 700,
              fontSize: 11,
              lineHeight: '15px',
              color: textColor,
            }}
          >
            Q2
          </span>
          <p
            className="flex-1"
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: 11,
              lineHeight: '15px',
              color: textColor,
            }}
          >
            {translated
              ? 'Ποιο είναι το μέγιστο ποσό που θα ήσασταν διατεθειμένοι να ξοδέψετε για αυτό το προϊόν/υπηρεσία;'
              : 'What is the maximum amount you would be willing to spend on this product/service?'}
          </p>
        </div>
      ),
    },
    {
      key: 'q4',
      width: 287,
      gradient: q4Gradient,
      delay: 3,
      globalIdx: 3,
      content: (translated) => (
        <div
          className="flex flex-col items-start w-full"
          style={{ gap: 12.44 }}
        >
          <div className="flex items-start w-full" style={{ gap: 8 }}>
            <span
              style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                lineHeight: '15px',
                color: textColor,
              }}
            >
              Q4
            </span>
            <p
              className="flex-1"
              style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: 11,
                lineHeight: '15px',
                color: textColor,
              }}
            >
              {translated
                ? 'Πόσο σημαντική είναι η τιμή όταν σκέφτεστε να αγοράσετε ένα νέο προϊόν/υπηρεσία;'
                : 'How important is price when you are considering purchasing a new product/service?'}
            </p>
          </div>
          <div
            className="flex flex-col items-start w-full"
            style={{ gap: 9.05 }}
          >
            <AnswerRow
              num="01"
              text={translated ? 'Εξαιρετικά σημαντική' : 'Extremely important'}
              textColor={textColor}
            />
            <AnswerRow
              num="02"
              text={translated ? 'Πολύ σημαντική' : 'Very important'}
              textColor={textColor}
            />
            <AnswerRow
              num="03"
              text={translated ? 'Μέτρια σημαντική' : 'Moderately important'}
              textColor={textColor}
            />
            <AnswerRow
              num="04"
              text={translated ? 'Ελαφρώς σημαντική' : 'Slightly important'}
              textColor={textColor}
            />
          </div>
        </div>
      ),
    },
  ];

  const isInitialReveal = phase === 'response';

  return (
    <>
      {/* Left column: Q1, Q3, Q5 */}
      <div
        className="absolute flex flex-col"
        style={{ left: -70, top: 80, gap: 12 }}
      >
        <AnimatePresence>
          {showQuestions &&
            englishLeft.map((q) => (
              <motion.div
                key={q.key}
                style={{ width: q.width }}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12, transition: { duration: 0.2 } }}
                transition={{
                  duration: 0.45,
                  delay: isInitialReveal ? q.delay * 0.4 + 0.6 : 0,
                }}
              >
                <QuestionCard
                  label={q.key}
                  gradient={q.gradient}
                  borderColor={borderColor}
                >
                  <CrossfadeContent
                    isTranslated={translatedIndices.has(q.globalIdx)}
                  >
                    {q.content}
                  </CrossfadeContent>
                </QuestionCard>
              </motion.div>
            ))}
        </AnimatePresence>
      </div>

      {/* Right column: Q2, Q4 */}
      <div
        className="absolute flex flex-col"
        style={{ left: 455, top: 181, gap: 12 }}
      >
        <AnimatePresence>
          {showQuestions &&
            englishRight.map((q) => (
              <motion.div
                key={q.key}
                style={{ width: q.width }}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12, transition: { duration: 0.2 } }}
                transition={{
                  duration: 0.45,
                  delay: isInitialReveal ? q.delay * 0.4 + 0.6 : 0,
                }}
              >
                <QuestionCard
                  label={q.key}
                  gradient={q.gradient}
                  borderColor={borderColor}
                >
                  <CrossfadeContent
                    isTranslated={translatedIndices.has(q.globalIdx)}
                  >
                    {q.content}
                  </CrossfadeContent>
                </QuestionCard>
              </motion.div>
            ))}
        </AnimatePresence>
      </div>
    </>
  );
}

/* ─── Tab Buttons ─── */
function TabButton({ icon, label }) {
  return (
    <div
      className="flex-1 flex items-center justify-center"
      style={{
        background: 'white',
        borderRadius: 3.43,
        boxShadow: '0px 2.288px 5.721px 0px rgba(66,66,66,0.08)',
        padding: '7.04px 6.71px',
        gap: 4.57,
      }}
    >
      {icon}
      <span
        style={{
          fontFamily: "'Roboto', sans-serif",
          fontSize: 9.84,
          lineHeight: '9.15px',
          color: '#323945',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </div>
  );
}

function TranslationIcon() {
  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: 9.33, height: 9.33 }}
    >
      <svg
        className="block w-full h-full"
        fill="none"
        viewBox="0 0 9.32958 9.32958"
      >
        <g clipPath="url(#clip_trans)">
          <path d={svgPaths.p28549800} fill="#231F3C" />
          <path d={svgPaths.pd3b8f0} fill="#D830D0" />
          <path d={svgPaths.p270cf280} fill="#231F3C" />
        </g>
        <defs>
          <clipPath id="clip_trans">
            <rect fill="white" width="9.32958" height="9.32958" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

function SetToneIcon() {
  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: 9.17, height: 9.33 }}
    >
      <svg
        className="block w-full h-full"
        fill="none"
        viewBox="0 0 9.17927 9.32958"
      >
        <g clipPath="url(#clip_tone)">
          <path d={svgPaths.p1544d500} fill="#D830D0" />
          <path d={svgPaths.p1cf58500} fill="#231F3C" />
          <path d={svgPaths.p2b4c0880} fill="#D830D0" />
        </g>
        <defs>
          <clipPath id="clip_tone">
            <rect fill="white" width="9.17927" height="9.32958" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

function RephraseIcon() {
  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: 9.1, height: 9.33 }}
    >
      <svg
        className="block w-full h-full"
        fill="none"
        viewBox="0 0 9.10074 9.32958"
      >
        <g clipPath="url(#clip_reph)">
          <path d={svgPaths.p34ccd300} fill="#D830D0" />
          <path d={svgPaths.p7bf00} fill="#D830D0" />
          <path d={svgPaths.p18452000} fill="#D830D0" />
          <path
            d={svgPaths.p709b080}
            fill="#231F3C"
            stroke="#231F3C"
            strokeWidth="0.088"
          />
          <path
            d={svgPaths.pe012300}
            fill="#231F3C"
            stroke="#231F3C"
            strokeWidth="0.088"
          />
        </g>
        <defs>
          <clipPath id="clip_reph">
            <rect fill="white" width="9.10074" height="9.32958" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

function SparklesIcon() {
  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: 19.45, height: 3.43 }}
    >
      <svg
        className="block w-full h-full"
        fill="none"
        viewBox="0 0 19.4511 3.4326"
      >
        <path d={svgPaths.p32001b00} fill="#323945" />
        <path d={svgPaths.pae81700} fill="#323945" />
        <path d={svgPaths.p31a82b00} fill="#323945" />
      </svg>
    </div>
  );
}

function AiIcon() {
  return (
    <div
      className="relative flex-shrink-0"
      style={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span
        style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 600,
          fontSize: 7,
          color: 'white',
        }}
      >
        AI
      </span>
    </div>
  );
}

function AiIconColor({ color = '#D830D0' }) {
  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: 14.146, height: 12.136 }}
    >
      <svg
        className="block w-full h-full"
        fill={color}
        preserveAspectRatio="none"
        viewBox="0 0 14.1462 12.1322"
      >
        <g>
          <path d={svgPathsAi.pb87f400} fill={color} />
          <g>
            <path d={svgPathsAi.p3f000e00} fill={color} />
            <path d={svgPathsAi.p1b30f980} fill={color} />
          </g>
          <path d={svgPathsAi.p14582900} fill={color} />
        </g>
      </svg>
    </div>
  );
}

/* ─── Thinking Dots Animation ─── */
function ThinkingDots() {
  return (
    <div className="flex items-center" style={{ gap: 4 }}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          style={{
            width: 4,
            height: 4,
            borderRadius: '50%',
            background: '#9198A7',
          }}
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{
            duration: 1,
            ease: 'easeInOut',
            repeat: Infinity,
            delay: i * 0.25,
          }}
        />
      ))}
    </div>
  );
}

/* ─── Typewriter Hook ─── */
function useTypewriter(text, active, speed = 35) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!active) {
      setDisplayed('');
      setDone(false);
      return;
    }
    setDisplayed('');
    setDone(false);
    let i = 0;
    const id = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(id);
        setDone(true);
      }
    }, speed);
    return () => clearInterval(id);
  }, [active, text, speed]);

  return { displayed, done };
}

/* ─── Input Field Component ─── */
function ChatInputField({ text, isPlaceholder }) {
  return (
    <div
      className="relative w-full flex-shrink-0"
      style={{ borderRadius: 4.57 }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{ border: '0.57px solid #c1c8d4', borderRadius: 4.57 }}
      />
      <div
        className="flex items-center justify-between"
        style={{ padding: '9.97px 9.84px' }}
      >
        <span
          style={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: 10.47,
            lineHeight: '13.87px',
            color: isPlaceholder ? '#9198a7' : '#323945',
          }}
        >
          {text}
        </span>
        <svg
          className="flex-shrink-0"
          style={{ width: 6.21, height: 8 }}
          fill="none"
          viewBox="0 0 6.21711 8.00928"
        >
          <g clipPath="url(#clip_enter)">
            <path d={svgPaths.pc35db00} fill="#9198A7" />
          </g>
          <defs>
            <clipPath id="clip_enter">
              <rect fill="white" width="6.21711" height="8.00928" />
            </clipPath>
          </defs>
        </svg>
      </div>
    </div>
  );
}

/* ─── AI Chat Panel ──── */

const USER_MSG = 'Help me find the right price for my product';
const TRANSLATE_MSG = 'Translate to Greek';
const AI_MSG =
  "I'm reviewing your request to find the right price and will provide helpful options as soon as possible.\n\nI have added a comprehensive set of questions to your survey, focusing on pricing strategy and customer perception of value.\n\nThis includes a Van Westendorp pricing analysis, questions about willingness to pay, price importance, value for money, purchase likelihood, factors influencing price perception, and open-ended questions to gather detailed feedback.\n\nDo you need any further assistance with your survey?";
const AI_TRANSLATE_MSG =
  "I've translated all questions in your survey to Greek. Each question and its answer options have been carefully localized.";

function AiChat({ phase, setPhase, onTranslationDone, animationsReady }) {
  const timerRef = useRef();

  const { displayed: typedUser, done: userDone } = useTypewriter(
    USER_MSG,
    phase === 'typing',
    50,
  );

  const { displayed: typedTranslate, done: translateDone } = useTypewriter(
    TRANSLATE_MSG,
    phase === 'translateTyping',
    50,
  );

  // Phase transitions
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (phase === 'idle') {
      if (!animationsReady) return undefined;
      timerRef.current = setTimeout(() => setPhase('typing'), 1000);
    } else if (phase === 'thinking') {
      timerRef.current = setTimeout(() => setPhase('response'), 1500);
    } else if (phase === 'response') {
      // After questions appear, start translate typing
      timerRef.current = setTimeout(() => setPhase('translateTyping'), 2000);
    } else if (phase === 'translateThinking') {
      timerRef.current = setTimeout(() => setPhase('translated'), 1500);
    } else if (phase === 'translated') {
      timerRef.current = setTimeout(() => onTranslationDone(), 3500);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [phase, animationsReady, onTranslationDone, setPhase]);

  // When typing finishes, move to thinking
  useEffect(() => {
    if (phase === 'typing' && userDone) {
      timerRef.current = setTimeout(() => setPhase('thinking'), 800);
    }
  }, [phase, userDone]);

  // When translate typing finishes, move to translateThinking
  useEffect(() => {
    if (phase === 'translateTyping' && translateDone) {
      timerRef.current = setTimeout(() => setPhase('translateThinking'), 800);
    }
  }, [phase, translateDone]);

  const isConversation =
    phase === 'thinking' ||
    phase === 'response' ||
    phase === 'translateTyping' ||
    phase === 'translateThinking' ||
    phase === 'translated';

  return (
    <div
      className="flex flex-col items-center overflow-hidden flex-shrink-0"
      style={{
        width: 323,
        height: 543,
        background: '#fefefe',
        borderRadius: 8.2,
        boxShadow: '0px 3.553px 92.389px 0px rgba(17,2,35,0.12)',
        paddingBottom: 12.44,
      }}
    >
      {/* Top section: header + tabs + sparkles */}
      <div className="flex flex-col items-center w-full" style={{ gap: 11.62 }}>
        {/* Header bar */}
        <div className="w-full flex-shrink-0 relative" style={{ height: 34 }}>
          <DarkGradientBar />
          <div
            className="relative flex items-center"
            style={{ padding: '12px 8.2px 12px 12px', gap: 5.72, zIndex: 1 }}
          >
            <span
              className="flex items-center"
              style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: 10.65,
                lineHeight: '9.15px',
                color: 'white',
                whiteSpace: 'nowrap',
              }}
            >
              <AiIconColor color="#ffffff" />
              <span className="margin-left-sm">Welcome to AI Builder</span>
            </span>
            <div className="flex-1" />
            <div
              className="flex-shrink-0"
              style={{ width: 9.15, height: 9.15 }}
            >
              <svg
                className="block w-full h-full"
                fill="none"
                viewBox="0 0 5.34 5.34"
              >
                <path
                  d={svgPaths.p14a53a00}
                  fill="white"
                  clipRule="evenodd"
                  fillRule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Tab buttons */}
        <div
          className="flex items-center w-full"
          style={{ padding: '0 9.15px', gap: 3.43 }}
        >
          <TabButton icon={<TranslationIcon />} label="Translate survey" />
          <TabButton icon={<SetToneIcon />} label="Set tone" />
          <TabButton icon={<RephraseIcon />} label="Rephrase" />
        </div>

        {/* Sparkles */}
        <div className="flex justify-center w-full">
          <SparklesIcon />
        </div>
      </div>

      {/* ═══ PHASE 1 & 2: Input at top ═══ */}
      {!isConversation && (
        <div
          className="flex flex-col items-start w-full flex-1"
          style={{ padding: '16px 13.9px 0' }}
        >
          <motion.div
            className="w-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <ChatInputField
              text={
                phase === 'typing'
                  ? typedUser ||
                    'Describe your request as detailed as possible...'
                  : 'Describe your request as detailed as possible...'
              }
              isPlaceholder={phase !== 'typing' || !typedUser}
            />
          </motion.div>
        </div>
      )}

      {/* ═══ PHASE 3+: Chat conversation ═══ */}
      {isConversation && (
        <div
          className="flex flex-col items-start w-full overflow-y-auto"
          style={{ padding: '16px 13.9px 0', flex: 1 }}
        >
          {/* User bubble #1 */}
          <motion.div
            className="flex flex-col items-end w-full"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className="flex flex-col items-end"
              style={{
                background: '#231f3c',
                borderRadius: 4.57,
                padding: '9px 9px 9px 10px',
                maxWidth: 246,
              }}
            >
              <p
                style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: 10.47,
                  lineHeight: '14.69px',
                  color: 'white',
                  margin: 0,
                }}
              >
                {USER_MSG}
              </p>
            </div>
          </motion.div>

          {/* AI thinking dots (phase 3 only) */}
          {phase === 'thinking' && (
            <motion.div
              className="flex items-start w-full"
              style={{ gap: 6.56, marginTop: 9.15 }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <AiIconColor />
              <div className="flex items-center" style={{ paddingTop: 2 }}>
                <ThinkingDots />
              </div>
            </motion.div>
          )}

          {/* AI response #1 (visible from phase 4 onward) */}
          {(phase === 'response' ||
            phase === 'translateTyping' ||
            phase === 'translateThinking' ||
            phase === 'translated') && (
            <motion.div
              className="flex items-start w-full"
              style={{ gap: 6.56, marginTop: 9.15 }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex-shrink-0" style={{ paddingTop: 2 }}>
                <AiIconColor />
              </div>
              <div
                className="flex-1 flex flex-col items-start"
                style={{
                  background: '#f5f5fb',
                  borderRadius: '0 6.56px 6.56px 6.56px',
                  paddingTop: 9.84,
                }}
              >
                <div
                  className="flex flex-col items-start"
                  style={{ padding: '0 9.84px 9.84px', gap: 13.12 }}
                >
                  <p
                    style={{
                      fontFamily: "'Roboto', sans-serif",
                      fontSize: 9.84,
                      lineHeight: '13.95px',
                      color: '#323945',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {AI_MSG}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* User bubble #2: "Translate to French" (visible from translateThinking onward) */}
          {(phase === 'translateThinking' || phase === 'translated') && (
            <motion.div
              className="flex flex-col items-end w-full"
              style={{ marginTop: 9.15 }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div
                className="flex flex-col items-end"
                style={{
                  background: '#231f3c',
                  borderRadius: 4.57,
                  padding: '9px 9px 9px 10px',
                  maxWidth: 246,
                }}
              >
                <p
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: 10.47,
                    lineHeight: '14.69px',
                    color: 'white',
                    margin: 0,
                  }}
                >
                  {TRANSLATE_MSG}
                </p>
              </div>
            </motion.div>
          )}

          {/* AI thinking dots for translate */}
          {phase === 'translateThinking' && (
            <motion.div
              className="flex items-start w-full"
              style={{ gap: 6.56, marginTop: 9.15 }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <AiIconColor />
              <div className="flex items-center" style={{ paddingTop: 2 }}>
                <ThinkingDots />
              </div>
            </motion.div>
          )}

          {/* AI response #2: translate confirmation */}
          {phase === 'translated' && (
            <motion.div
              className="flex items-start w-full"
              style={{ gap: 6.56, marginTop: 9.15 }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex-shrink-0" style={{ paddingTop: 2 }}>
                <AiIconColor />
              </div>
              <div
                className="flex-1 flex flex-col items-start"
                style={{
                  background: '#f5f5fb',
                  borderRadius: '0 6.56px 6.56px 6.56px',
                  paddingTop: 9.84,
                }}
              >
                <div
                  className="flex flex-col items-start"
                  style={{ padding: '0 9.84px 9.84px', gap: 13.12 }}
                >
                  <p
                    style={{
                      fontFamily: "'Roboto', sans-serif",
                      fontSize: 9.84,
                      lineHeight: '13.95px',
                      color: '#323945',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {AI_TRANSLATE_MSG}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Input field below content */}
          <div className="w-full flex-shrink-0" style={{ marginTop: 16 }}>
            <ChatInputField
              text={
                phase === 'translateTyping'
                  ? typedTranslate ||
                    'Describe your request as detailed as possible...'
                  : 'Describe your request as detailed as possible...'
              }
              isPlaceholder={phase !== 'translateTyping' || !typedTranslate}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Logos ─���─ */
function Logos() {
  const isDarkMode = false;
  return (
    <div className="flex flex-col items-center" style={{ gap: 16 }}>
      <p
        style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 500,
          fontSize: 11,
          color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(50,57,69,0.5)',
          textAlign: 'center',
        }}
      >
        Trusted By
      </p>
      <div
        className={`flex items-center ${isDarkMode ? 'opacity-60' : 'opacity-40 brightness-0'}`}
        style={{ gap: 30 }}
      >
        <div className="opacity-60" style={{ width: 115, height: 14 }}>
          <img
            alt="DoorDash"
            className="w-full h-full object-contain"
            src={doordashLogo}
          />
        </div>
        <div className="opacity-60" style={{ width: 80, height: 20 }}>
          <img
            alt="Energizer"
            className="w-full h-full object-contain"
            src={energizerLogo}
          />
        </div>
        <div className="opacity-60" style={{ width: 80, height: 20 }}>
          <img
            alt="Gillette"
            className="w-full h-full object-contain"
            src={gilletteLogo}
          />
        </div>
        <div className="opacity-60" style={{ width: 60, height: 20 }}>
          <img
            alt="Hulu"
            className="w-full h-full object-contain"
            src={huluLogo}
          />
        </div>
        <div className="opacity-60" style={{ width: 67, height: 20 }}>
          <img
            alt="Nestle"
            className="w-full h-full object-contain"
            src={nestleLogo}
          />
        </div>
        <div className="opacity-60" style={{ width: 84, height: 20 }}>
          <img
            alt="Walmart"
            className="w-full h-full object-contain"
            src={walmartLogo}
          />
        </div>
      </div>
    </div>
  );
}

/* ─── Middle Screen ─── */
function MiddleScreen() {
  const isDarkMode = false;
  const [counts, setCounts] = useState([0, 0, 0, 0, 0, 0]);
  const [total, setTotal] = useState(0);
  const targetCounts = [43, 60, 266, 312, 267, 54];
  const targetTotal = 1002;

  useEffect(() => {
    const interval = setInterval(() => {
      setCounts((prev) =>
        prev.map((c, i) => {
          const target = targetCounts[i];
          if (c < target) {
            return Math.min(c + Math.ceil((target - c) / 20), target);
          }
          return c;
        }),
      );
      setTotal((prev) =>
        prev < targetTotal
          ? Math.min(prev + Math.ceil((targetTotal - prev) / 20), targetTotal)
          : prev,
      );
    }, 70);

    return () => clearInterval(interval);
  }, []);

  const percentages = counts.map((c) =>
    total > 0 ? ((c / total) * 100).toFixed(2) : '0.00',
  );

  const barColors = [
    '#67B383',
    '#5BA8D8',
    '#2F99B8',
    '#9CA3AF',
    '#E8945D',
    '#DFD16D',
  ];
  const textColor = isDarkMode ? '#E5E7EB' : '#323945';
  const subTextColor = isDarkMode ? '#9CA3AF' : '#6B7280';
  const bgColor = isDarkMode ? 'rgba(255, 255, 255, 0.04)' : '#FFFFFF';
  const borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#E5E7E9';

  return (
    <div
      className="absolute flex flex-col items-center"
      style={{
        left: 'calc(50% + 0.05px)',
        top: 205,
        transform: 'translateX(-50%)',
        width: 520,
      }}
    >
      <div
        className="relative flex flex-col overflow-hidden"
        style={{
          width: 520,
          height: 543,
          background: bgColor,
          border: `1px solid ${borderColor}`,
          borderRadius: 10,
          padding: '28px',
          backdropFilter: 'blur(12px)',
          boxShadow: '0px 3.553px 92.389px 0px rgba(17,2,35,0.12)',
        }}
      >
        {/* Question Header */}
        <div className="mb-4">
          <p
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontSize: 10,
              color: subTextColor,
              marginBottom: 4,
            }}
          >
            Q6: How many televisions do you have in your household?
          </p>
        </div>

        {/* Data Table */}
        <div
          className="flex flex-col w-full mb-4"
          style={{
            border: `1px solid ${borderColor}`,
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            className="flex"
            style={{
              background: isDarkMode ? 'rgba(255,255,255,0.05)' : '#F9FAFB',
              padding: '8px 14px',
              borderBottom: `1px solid ${borderColor}`,
            }}
          >
            <div style={{ flex: 1 }}>
              <p
                style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: 9,
                  fontWeight: 600,
                  color: subTextColor,
                }}
              >
                Answers
              </p>
            </div>
            <div style={{ width: 100, textAlign: 'right' }}>
              <p
                style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: 9,
                  fontWeight: 600,
                  color: subTextColor,
                }}
              >
                Responded (%)
              </p>
            </div>
            <div style={{ width: 70, textAlign: 'right' }}>
              <p
                style={{
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: 9,
                  fontWeight: 600,
                  color: subTextColor,
                }}
              >
                Count
              </p>
            </div>
          </div>

          {/* Rows */}
          {counts.map((count, i) => (
            <div
              key={i}
              className="flex items-center"
              style={{
                padding: '7px 14px',
                borderBottom: i < 5 ? `1px solid ${borderColor}` : 'none',
              }}
            >
              <div style={{ flex: 1 }}>
                <p
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: 10,
                    color: textColor,
                  }}
                >
                  {i + 1}
                </p>
              </div>
              <div style={{ width: 100, textAlign: 'right' }}>
                <p
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: 10,
                    color: textColor,
                  }}
                >
                  {percentages[i]}%
                </p>
              </div>
              <div style={{ width: 70, textAlign: 'right' }}>
                <p
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: 10,
                    color: textColor,
                  }}
                >
                  {count}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Completed Count */}
        <div className="flex justify-end mb-4">
          <p
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontSize: 9,
              color: subTextColor,
            }}
          >
            COMPLETED:{' '}
            <span style={{ fontWeight: 600, color: textColor }}>
              {total.toLocaleString()}
            </span>
          </p>
        </div>

        {/* Bar Chart Section */}
        <div className="flex flex-col" style={{ gap: 8 }}>
          {/* Chart Container */}
          <div
            className="relative"
            style={{
              height: 200,
              paddingLeft: 32,
              paddingRight: 8,
              paddingTop: 8,
              paddingBottom: 28,
            }}
          >
            {/* Y-axis values */}
            <div
              className="absolute flex flex-col justify-between"
              style={{ left: 6, top: 8, bottom: 28, width: 22 }}
            >
              {[400, 300, 200, 100, 0].map((val) => (
                <p
                  key={val}
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: 8,
                    color: subTextColor,
                    textAlign: 'right',
                  }}
                >
                  {val}
                </p>
              ))}
            </div>

            {/* Grid lines */}
            <div
              className="absolute"
              style={{ left: 32, right: 8, top: 8, bottom: 28 }}
            >
              {[0, 25, 50, 75, 100].map((pct) => (
                <div
                  key={pct}
                  className="absolute w-full"
                  style={{
                    bottom: `${pct}%`,
                    height: 1,
                    background: isDarkMode
                      ? 'rgba(255,255,255,0.05)'
                      : '#E5E7E9',
                  }}
                />
              ))}
            </div>

            {/* Bars */}
            <div
              className="absolute flex items-end justify-between"
              style={{ left: 38, right: 14, bottom: 28, height: 156 }}
            >
              <div
                className="flex flex-col items-center justify-end"
                style={{ width: 60, height: '100%' }}
              >
                <motion.div
                  style={{
                    width: 48,
                    height: 156,
                    background: barColors[0],
                    borderRadius: '2px 2px 0 0',
                    transformOrigin: 'bottom',
                  }}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: counts[0] / 400 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </div>
              <div
                className="flex flex-col items-center justify-end"
                style={{ width: 60, height: '100%' }}
              >
                <motion.div
                  style={{
                    width: 48,
                    height: 156,
                    background: barColors[1],
                    borderRadius: '2px 2px 0 0',
                    transformOrigin: 'bottom',
                  }}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: counts[1] / 400 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </div>
              <div
                className="flex flex-col items-center justify-end"
                style={{ width: 60, height: '100%' }}
              >
                <motion.div
                  style={{
                    width: 48,
                    height: 156,
                    background: barColors[2],
                    borderRadius: '2px 2px 0 0',
                    transformOrigin: 'bottom',
                  }}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: counts[2] / 400 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </div>
              <div
                className="flex flex-col items-center justify-end"
                style={{ width: 60, height: '100%' }}
              >
                <motion.div
                  style={{
                    width: 48,
                    height: 156,
                    background: barColors[3],
                    borderRadius: '2px 2px 0 0',
                    transformOrigin: 'bottom',
                  }}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: counts[3] / 400 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </div>
              <div
                className="flex flex-col items-center justify-end"
                style={{ width: 60, height: '100%' }}
              >
                <motion.div
                  style={{
                    width: 48,
                    height: 156,
                    background: barColors[4],
                    borderRadius: '2px 2px 0 0',
                    transformOrigin: 'bottom',
                  }}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: counts[4] / 400 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </div>
              <div
                className="flex flex-col items-center justify-end"
                style={{ width: 60, height: '100%' }}
              >
                <motion.div
                  style={{
                    width: 48,
                    height: 156,
                    background: barColors[5],
                    borderRadius: '2px 2px 0 0',
                    transformOrigin: 'bottom',
                  }}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: counts[5] / 400 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </div>
            </div>

            {/* X-axis labels */}
            <div
              className="absolute flex justify-between"
              style={{ left: 38, right: 14, bottom: 6 }}
            >
              <div style={{ width: 60, textAlign: 'center' }}>
                <p
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: 8,
                    color: subTextColor,
                  }}
                >
                  1
                </p>
              </div>
              <div style={{ width: 60, textAlign: 'center' }}>
                <p
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: 8,
                    color: subTextColor,
                  }}
                >
                  2
                </p>
              </div>
              <div style={{ width: 60, textAlign: 'center' }}>
                <p
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: 8,
                    color: subTextColor,
                  }}
                >
                  3
                </p>
              </div>
              <div style={{ width: 60, textAlign: 'center' }}>
                <p
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: 8,
                    color: subTextColor,
                  }}
                >
                  4
                </p>
              </div>
              <div style={{ width: 60, textAlign: 'center' }}>
                <p
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: 8,
                    color: subTextColor,
                  }}
                >
                  5
                </p>
              </div>
              <div style={{ width: 60, textAlign: 'center' }}>
                <p
                  style={{
                    fontFamily: "'Roboto', sans-serif",
                    fontSize: 8,
                    color: subTextColor,
                  }}
                >
                  6
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Design canvas (original Figma export size) ─── */
const DESIGN_WIDTH = 815;
const DESIGN_HEIGHT = 942;

function useCarouselReady() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let raf1;
    let raf2;

    const markReady = () => {
      if (!cancelled) setReady(true);
    };

    const waitForPaint = () => {
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(markReady);
      });
    };

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(waitForPaint).catch(waitForPaint);
    } else {
      waitForPaint();
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, []);

  return ready;
}

function useCarouselScale(containerRef) {
  const [scales, setScales] = useState({ fitScale: 1, coverScale: 1 });
  const rafRef = useRef();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const updateScale = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const width = el.clientWidth;
        const height = el.clientHeight;
        if (width <= 0) return;

        const scaleX = width / DESIGN_WIDTH;
        const scaleY = height > 0 ? height / DESIGN_HEIGHT : scaleX;
        setScales({
          fitScale: Math.min(scaleX, scaleY),
          coverScale: Math.max(scaleX, scaleY),
        });
      });
    };

    updateScale();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateScale);
      observer.observe(el);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateScale);
    return () => {
      window.removeEventListener('resize', updateScale);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [containerRef]);

  return scales;
}

function CarouselBackdrop({ animationsReady }) {
  return (
    <>
      <div
        className="absolute left-0 top-0 flex items-center justify-center"
        style={{ width: DESIGN_WIDTH, height: DESIGN_HEIGHT }}
      >
        <div className="flex-none" style={{ transform: 'rotate(-90deg)' }}>
          <div
            className="transition-colors duration-500"
            style={{
              width: DESIGN_HEIGHT,
              height: DESIGN_WIDTH,
              background: '#F9FAFC',
            }}
          />
        </div>
      </div>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, #f5f5f5 60%, #e8e0ec 100%)',
        }}
      />
      <ShadowEllipse active={animationsReady} />
    </>
  );
}

/* ─── Main App ─── */
export default function FeaturedCarousel() {
  const containerRef = useRef(null);
  const animationsReady = useCarouselReady();
  const { fitScale, coverScale } = useCarouselScale(containerRef);
  const [phase, setPhase] = useState('idle');
  const [activeSlide, setActiveSlide] = useState(0);
  const isDarkMode = false;
  const slideTimerRef = useRef();

  const handleTranslationDone = useCallback(() => {
    setActiveSlide((prev) => (prev === 0 ? 1 : prev));
  }, []);

  useEffect(() => {
    if (activeSlide === 1) {
      slideTimerRef.current = setTimeout(() => {
        setActiveSlide(2);
      }, 4000);
    } else if (activeSlide === 2) {
      slideTimerRef.current = setTimeout(() => {
        setActiveSlide(0);
        setPhase('idle');
      }, 6500);
    }
    return () => {
      if (slideTimerRef.current) clearTimeout(slideTimerRef.current);
    };
  }, [activeSlide]);

  const textColor = '#323945';
  const subtextColor = 'rgba(50,57,69,0.7)';

  return (
    <div ref={containerRef} className="featured-carousel-container">
      <div className="featured-carousel-backdrop" aria-hidden="true">
        <div
          className="featured-carousel-backdrop__canvas"
          style={{
            width: DESIGN_WIDTH,
            height: DESIGN_HEIGHT,
            transform: `translate3d(-50%, -50%, 0) scale(${coverScale})`,
          }}
        >
          <CarouselBackdrop animationsReady={animationsReady} />
        </div>
      </div>

      <div className="featured-carousel-stage">
        <div
          className="featured-carousel-stage__inner"
          style={{
            width: DESIGN_WIDTH * fitScale,
            height: DESIGN_HEIGHT * fitScale,
          }}
        >
          <div
            className="featured-carousel-root relative overflow-hidden transition-colors duration-500"
            style={{
              width: DESIGN_WIDTH,
              height: DESIGN_HEIGHT,
              transform: `scale(${fitScale}) translateZ(0)`,
              transformOrigin: 'top left',
            }}
          >
            {/* Dot Navigation */}
            <div
              className="absolute z-50 flex items-center gap-2"
              style={{ top: 40, left: '50%', transform: 'translateX(-50%)' }}
            >
              {[0, 1, 2].map((i) => (
                <button
                  key={i}
                  onClick={() => {
                    setActiveSlide(i);
                    if (i === 0) setPhase('idle');
                  }}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: 6,
                    height: 6,
                    backgroundColor:
                      activeSlide === i
                        ? 'rgba(50, 57, 69, 1)'
                        : 'rgba(50, 57, 69, 0.3)',
                    cursor: 'pointer',
                    border: 'none',
                    padding: 0,
                  }}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            {/* Titles (Instant transition) */}
            <div
              className="absolute z-20 w-full pointer-events-none"
              style={{ top: 80, left: 0 }}
            >
              {activeSlide === 0 && (
                <div className="flex flex-col items-center" style={{ gap: 6 }}>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 600,
                      fontSize: 24,
                      lineHeight: '37px',
                      color: textColor,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Build Surveys with AI
                  </p>
                  <p
                    style={{
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 300,
                      fontSize: 13,
                      lineHeight: '19px',
                      color: subtextColor,
                      textAlign: 'center',
                    }}
                  >
                    Generate a complete questionnaire in seconds
                    <br />
                    using natural language.
                  </p>
                </div>
              )}
              {activeSlide === 1 && (
                <div className="flex flex-col items-center" style={{ gap: 6 }}>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 600,
                      fontSize: 24,
                      lineHeight: '37px',
                      color: textColor,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Analyze responses in real-time
                  </p>
                  <p
                    style={{
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 300,
                      fontSize: 13,
                      lineHeight: '19px',
                      color: subtextColor,
                      textAlign: 'center',
                    }}
                  >
                    Watch the data flow in and get instant
                    <br />
                    insights from your audience.
                  </p>
                </div>
              )}
              {activeSlide === 2 && (
                <div className="flex flex-col items-center justify-center">
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 600,
                      fontSize: 24,
                      lineHeight: '34px',
                      color: textColor,
                      textAlign: 'center',
                      width: 313,
                    }}
                  >
                    Turn results into instant AI-powered reports
                  </p>
                </div>
              )}
            </div>

            <AnimatePresence mode="wait">
              {activeSlide === 0 && (
                <motion.div
                  key="slide-0"
                  className="absolute inset-0"
                  initial={{ opacity: 0, y: 20, scale: 0.98 }}
                  animate={
                    animationsReady
                      ? { opacity: 1, y: 0, scale: 1 }
                      : { opacity: 0, y: 20, scale: 0.98 }
                  }
                  exit={{ opacity: 0, y: -20, scale: 1.02 }}
                  transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
                >
                  {/* Image section: survey + chat */}
                  <div
                    className="absolute flex flex-col items-center"
                    style={{
                      left: 'calc(50% + 0.05px)',
                      top: 205,
                      transform: 'translateX(-50%)',
                      width: 581,
                    }}
                  >
                    {/* Survey + Chat container */}
                    <div
                      className="relative"
                      style={{ width: 581, height: 543 }}
                    >
                      <SurveyPanel phase={phase} />
                      <div
                        className="absolute"
                        style={{
                          left: '50%',
                          top: 0,
                          transform: 'translateX(-50%) scale(1.08)',
                          transformOrigin: 'top center',
                          zIndex: 10,
                        }}
                      >
                        <AiChat
                          phase={phase}
                          setPhase={setPhase}
                          onTranslationDone={handleTranslationDone}
                          animationsReady={animationsReady}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeSlide === 1 && (
                <motion.div
                  key="slide-1"
                  className="absolute inset-0"
                  initial={{ opacity: 0, y: 20, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 1.02 }}
                  transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
                >
                  <MiddleScreen />
                </motion.div>
              )}

              {activeSlide === 2 && (
                <motion.div
                  key="slide-2"
                  className="absolute inset-0"
                  initial={{ opacity: 0, y: 20, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 1.02 }}
                  transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
                >
                  {/* Report Card */}
                  <div
                    className="absolute"
                    style={{
                      left: '50%',
                      bottom: 167,
                      transform: 'translateX(-50%)',
                    }}
                  >
                    <ReportCard />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Logos */}
            <div
              className="absolute"
              style={{ left: '50%', top: 835, transform: 'translateX(-50%)' }}
            >
              <Logos />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
